
import { defineComponent, ref, reactive, toRefs, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import f10ApiUS from '@/api/f10ApiUS'
import { isLocalDev } from '@/api/config'
import Table from '@/components/table.vue'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import * as echarts from 'echarts'

// 财务指标定义
const financialMetrics = [
  { label: '归母净利润 (元)', value: 'npParentCompanyOwners', unit: '元', format: 'currency' },
  { label: '营业收入 (元)', value: 'operatingRevenue', unit: '元', format: 'currency' },
  { label: '每股收益 (元)', value: 'basicEps', unit: '元', format: 'decimal' },
  { label: '每股净资产 (元)', value: 'netAssetPs', unit: '元', format: 'decimal' },
  { label: '毛利率', value: 'grossIncomeRatio', unit: '%', format: 'percent' },
  { label: '净利率', value: 'netProfitRatio', unit: '%', format: 'percent' },
  { label: 'ROE', value: 'roe', unit: '%', format: 'percent' },
  {
    label: '资产负债率',
    value: 'debtAssetsRatio',
    unit: '%',
    width: '120px',
    format: 'decimal'
  }
];

// 区间定义
const intervalDefinitions = {
  marketValue: ['<10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '1000亿~5000亿', '5000亿~10000亿', '≥10000亿'],
  totalAsset: ['<10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '1000亿~5000亿', '5000亿~10000亿', '≥10000亿'],
  npParentCompanyOwners: ['<0', '0~5000万', '5000万～1亿', '1～5亿', '5～10亿', '10～50亿', '50～100亿', '≥100亿'],
  roe: ['<0', '0~5%', '5%～10%', '10%~15%', '15%～20%', '20%~25%', '25%~30%', '≥30%'],
  netProfitRatio: ['<0', '0~10%', '10%～15%', '15%~20%', '20%～30%', '30%~40%', '40%～50%', '≥50%'],
  dividendRatio: ['=0', '0~1%', '1%～3%', '3%~5%', '5%～8%', '8%~10%', '≥10%'],
  equityMultiplierDupont: ['<1.5', '1.5~2', '2~3', '3~5', '5~10', '10~15', '15~20', '≥20'],
  assetTRate: ['0~0.05', '0.05~0.1', '0.1~0.2', '0.2~0.4', '0.4~0.6', '0.6~0.8', '0.8~1', '≥1'],
  debtAssetsRatio: ['<10%', '10~30%', '30~50%', '50~70%', '70～80%', '80～90%', '90～95%', '≥95%'],
  operatingRevenue: ['<1亿', '1亿~5亿', '5亿~10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '≥1000亿']
};

const baseColumns = [
  {
    title: '排名',
    dataIndex: 'rank',
    key: 'rank',
    width: '80px',
    align: 'center'
  },
  {
    title: '代码',
    dataIndex: 'symbol',
    key: 'symbol',
    width: '100px',
    align: 'left'
  },
  {
    title: '名称',
    dataIndex: 'name',
    key: 'name',
    width: '120px',
    ellipsis: true,
    align: 'left'
  }
];

export default defineComponent({
  props: {
    token: {
      type: String,
      default: () => ''
    },
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: () => ''
    }
  },
  components: { Table, Charts, Nodata },
  setup(props) {
    const pageData: any = reactive({
      // 财务数据
      rawData: [],
      allData: [], // 存储所有数据
      tableData: [],
      tableColumns: [],

      // 分页相关
      total: 0,
      pageSize: 10, // 每页显示条数
      currentPage: 1, // 当前页码

      // 加载状态
      spinning: true
    });

    // 格式化数值
    const formatValue = (value: any, format: string) => {
      if (value === null || value === undefined) return '—';

      const num = Number(value);
      if (isNaN(num)) return '—';

      switch (format) {
        case 'currency':
          if (Math.abs(num) >= 100000000) {
            return (num / 100000000).toFixed(2) + '亿';
          } else if (Math.abs(num) >= 10000) {
            return (num / 10000).toFixed(2) + '万';
          }
          return num.toFixed(2);
        case 'decimal':
          return num.toFixed(2);
        case 'percent':
          return (num * 100).toFixed(2) + '%';
        default:
          return num.toString();
      }
    };

    // 格式化日期
    const formatDate = (timestamp: number) => {
      return window.moment(timestamp).format('YYYY-MM-DD') // YYYY-MM-DD 格式
    };

    // 本地分页计算当前页数据
    const pagedData = computed(() => {
      const start = (pageData.currentPage - 1) * pageData.pageSize
      const end = start + pageData.pageSize
      return pageData.allData.slice(start, end)
    })

    // 分页配置
    const pagination = reactive({
      get total() {
        return pageData.total
      },
      get current() {
        return pageData.currentPage
      },
      set current(val: number) {
        pageData.currentPage = val
      },
      get pageSize() {
        return pageData.pageSize
      },
      set pageSize(val: number) {
        pageData.pageSize = val
      },
      showSizeChanger: true,
      showTotal: (total: number) => `共${total}条`,
      pageSizeOptions: ['10', '20', '50', '100'],
      onChange: (page: number, pageSize?: number) => {
        pageData.currentPage = page
        if (pageSize) pageData.pageSize = pageSize
      },
      onShowSizeChange: (current: number, size: number) => {
        pageData.currentPage = 1
        pageData.pageSize = size
      }
    })




    // 获取行业排名数据
    const getInfoF10IndustryPlace = () => {
      pageData.spinning = true;

      const route = useRoute()

      // 本地开发环境使用默认参数，否则使用路由参数
      let market: string, symbol: string
      if (isLocalDev) {
        market = 'NASDAQ'
        symbol = 'AAPL'
      } else {
        market = route.query.market as string || 'NASDAQ'
        symbol = route.query.code as string || 'AAPL'
      }

      const requestData = {
        data: {
          market,
          symbol,
          indexCode: 'npParentCompanyOwners,operatingRevenue,basicEps,netAssetPs,netProfitRatio,grossIncomeRatio,roe,debtAssetsRatio'
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944',
        }
      };

      f10ApiUS.getCwzb(requestData)
        .then((res: any) => {
          if (res?.data) {
            processApiData(res.data);
          } else {
            pageData.data = [];
          }
          pageData.spinning = false;
        })
        .catch(() => {
          pageData.spinning = false;
        });
    };

    // 处理API数据
    const processApiData = (data: any) => {
      pageData.rawData = data.list || [];

      // 创建表格列
      const baseColumns = [
        {
          title: '报告期\\指标',
          dataIndex: 'reportDate',
          key: 'reportDate',
          width: '120px',
          customRender: ({ text }: { text: number }) => formatDate(text)
        }
      ];

      const metricColumns = financialMetrics.map(metric => ({
        title: metric.label,
        dataIndex: metric.value,
        key: metric.value,
        width: '120px',
        align: 'right' as const,
        customRender: ({ record }: { record: any }) => {
          const value = record.index?.[metric.value]?.value;
          // 资产负债率特殊处理，直接显示数值+%
          if (metric.value === 'debtAssetsRatio') {
            return value ? value.toFixed(2) + '%' : '—';
          }
          return formatValue(value, metric.format);
        }
      }));

      pageData.tableColumns = baseColumns.concat(metricColumns as any);

      // 处理表格数据，按报告期降序排序（最新的在前面）
      pageData.allData = pageData.rawData
        .map((item: any, index: number) => ({
          key: index,
          reportDate: item.reportDate,
          index: item.index
        }))
        .sort((a: any, b: any) => b.reportDate - a.reportDate); // 按报告期降序排序

      console.log(pageData.allData, 'pageData.allData');

      // 更新分页信息
      pageData.total = pageData.allData.length

    };

    onMounted(() => {
      getInfoF10IndustryPlace();
    });

    return {
      ...toRefs(pageData),
      pagedData,
      pagination
    } as any;
  }
});
